/**
 * PDF Document Comparison Utility Module
 *
 * This module provides comprehensive PDF document comparison functionality using the
 * Longest Common Subsequence (LCS) algorithm. It performs multi-level comparison:
 * 1. Page-level alignment: Matches pages between source and target documents
 * 2. Line-level alignment: Matches lines within aligned pages
 * 3. Word-level diff: Identifies specific word-level changes (insert, delete, replace)
 *
 * The algorithm builds a complete change history showing exactly what was added,
 * removed, or modified at each level of the document hierarchy.
 */

import { EDocumentComparisonChangeType } from '../../constants'
import { EPDFPageStatus } from '../constants'
import {
  IPDFCompareChangeItem,
  IPDFLine,
  IPDFPage,
  IPDFParserResult,
  IPDFWordOp,
  TAlignmentEntry,
  TBbox,
  TLineInfo,
  TPageInfo,
} from '../types'

/**
 * Normalizes text by collapsing multiple whitespace characters into single spaces
 * and trimming leading/trailing whitespace.
 *
 * @param text - Raw text to normalize
 * @returns Normalized text for consistent comparison
 *
 * @example
 * normalizeText('  hello    world  ') // Returns 'hello world'
 */
const normalizeText = (text: string) => {
  return text.split(/\s+/g).join(' ').trim()
}

/**
 * Extracts and concatenates text from all spans in a PDF line.
 * Handles missing text gracefully by defaulting to empty strings.
 *
 * @param line - PDF line object containing text spans
 * @returns Concatenated text from all spans in the line
 */
const extractLineText = (line: IPDFLine) => {
  return line.spans.map((span) => span.text ?? '').join('')
}

const buildWordEntriesFromLine = (line: IPDFLine) => {
  const entries: Array<{ word: string; bbox: TBbox }> = []

  line.spans.forEach((span) => {
    const spanText = span.text ?? ''
    if (!spanText.trim()) return

    const spanLength = spanText.length
    if (!spanLength) return

    const [x0, y0, x1, y1] = span.bbox
    const width = x1 - x0
    const regex = /\S+/g
    let match = regex.exec(spanText)

    while (match) {
      const word = match[0]
      const start = match.index
      const end = start + word.length
      const startX = x0 + (width * start) / spanLength
      const endX = x0 + (width * end) / spanLength

      entries.push({
        word,
        bbox: [Math.min(startX, endX), y0, Math.max(startX, endX), y1],
      })
      match = regex.exec(spanText)
    }
  })

  return entries
}

/**
 * Extracts all lines from a PDF page and enriches them with metadata.
 * Filters out empty lines and organizes text into normalized form for comparison.
 *
 * Process:
 * 1. Iterates through all blocks in the page
 * 2. For each line, extracts text and normalizes it
 * 3. Splits normalized text into words for word-level comparison
 * 4. Preserves line bounding box for document positioning
 * 5. Skips empty lines to reduce noise
 *
 * @param page - PDF page object containing blocks and lines
 * @returns Array of enriched line info objects with text, words, and positioning
 */
const extractPageLines = (page: IPDFPage): TLineInfo[] => {
  const lines: TLineInfo[] = []
  let lineIndex = 0

  // Iterate through each block in the page
  page.blocks.forEach((block) => {
    if (!block.lines?.length) return

    // Process each line within the block
    block.lines.forEach((line) => {
      const wordEntries = buildWordEntriesFromLine(line)
      const words = wordEntries.map((entry) => entry.word)
      const wordBboxes = wordEntries.map((entry) => entry.bbox)
      const normalizedText = normalizeText(extractLineText(line))
      const bbox = line.bbox
      const currentLineIndex = lineIndex
      lineIndex += 1

      // Skip empty lines to reduce comparison noise
      if (!normalizedText) return

      lines.push({
        lineIndex: currentLineIndex,
        text: normalizedText,
        normalizedText,
        words,
        bbox,
        wordBboxes,
      })
    })
  })

  return lines
}

/**
 * Builds enriched page info by extracting all lines and combining them.
 * Creates a normalized text representation of the entire page for page-level comparison.
 *
 * @param page - PDF page object
 * @returns Page info containing individual lines and combined normalized text
 */
const buildPageInfo = (page: IPDFPage): TPageInfo => {
  const lines = extractPageLines(page)
  const text = lines.map((line) => line.text).join('\n')
  const normalizedText = normalizeText(text)
  return {
    pageIndex: page.page,
    text,
    normalizedText,
    lines,
  }
}

/**
 * Builds a dynamic programming table for computing the Longest Common Subsequence (LCS).
 * Uses bottom-up memoization to efficiently find longest matching subsequence between two sequences.
 *
 * Algorithm:
 * - dp[i][j] = length of LCS between source[i..end] and target[j..end]
 * - Iterates backwards through both sequences to build the table
 * - If elements match, extends previous LCS length by 1
 * - Otherwise, takes maximum of excluding current source or target element
 *
 * Time Complexity: O(m × n) where m = source length, n = target length
 * Space Complexity: O(m × n) for the DP table
 *
 * @param source - Source sequence to compare
 * @param target - Target sequence to compare
 * @param isEqual - Function to determine element equality (customizable comparison)
 * @returns 2D DP table where dp[i][j] = LCS length from position i in source and j in target
 */
const buildLcsTable = <T>(
  source: T[],
  target: T[],
  isEqual: (a: T, b: T) => boolean
) => {
  const sourceLength = source.length
  const targetLength = target.length

  // Initialize DP table with zeros
  const dp = new Array(sourceLength + 1)
    .fill(null)
    .map(() => new Array(targetLength + 1).fill(0))

  // Build DP table from bottom-right to top-left
  for (let i = sourceLength - 1; i >= 0; i -= 1) {
    for (let j = targetLength - 1; j >= 0; j -= 1) {
      // If elements match, extend the LCS from the next position
      if (isEqual(source[i], target[j])) dp[i][j] = dp[i + 1][j + 1] + 1
      // If elements don't match, take the best result from removing either element
      else dp[i][j] = Math.max(dp[i + 1][j], dp[i][j + 1])
    }
  }

  return dp
}

/**
 * Backtracks through the LCS DP table to find matching element pairs.
 * Uses a greedy approach: when elements match, record the match and advance both pointers.
 * When elements don't match, advance the pointer with the larger DP value.
 *
 * Algorithm:
 * - Starts at position [0][0] of the sequences
 * - When elements match, records the match and moves to next element in both sequences
 * - When elements don't match, follows the path that gave the larger LCS length
 *
 * @param source - Source sequence
 * @param target - Target sequence
 * @param isEqual - Equality comparison function
 * @param dp - Precomputed LCS DP table from buildLcsTable
 * @returns Array of matched element pairs with their indices
 */
const collectLcsMatches = <T>(
  source: T[],
  target: T[],
  isEqual: (a: T, b: T) => boolean,
  dp: number[][]
) => {
  const matches: Array<{ sourceIndex: number; targetIndex: number }> = []
  let i = 0
  let j = 0

  // Traverse both sequences and collect matching elements
  while (i < source.length && j < target.length) {
    if (isEqual(source[i], target[j])) {
      // Elements match: record the match and advance both pointers
      matches.push({ sourceIndex: i, targetIndex: j })
      i += 1
      j += 1
      continue
    }

    // Elements don't match: follow the path with better LCS length
    if (dp[i + 1][j] >= dp[i][j + 1]) i += 1
    else j += 1
  }

  return matches
}

/**
 * Builds a complete alignment between source and target sequences by filling gaps
 * between matched elements. Assigns status to each element pair:
 * - EQUAL: Matched elements that are unchanged
 * - CHANGED: Aligned but different elements
 * - DELETED: Elements in source with no target match
 * - INSERTED: Elements in target with no source match
 *
 * Algorithm:
 * 1. Iterates through matches in order
 * 2. For each gap between matches, creates alignment entries for unmatched elements
 * 3. Pairs elements from source and target in the gap by position (greedy pairing)
 * 4. Adds remaining unmatched elements at the end with appropriate status
 *
 * @param sourceLength - Total elements in source sequence
 * @param targetLength - Total elements in target sequence
 * @param matches - Array of matched element pairs from collectLcsMatches
 * @returns Complete alignment with status for each element pair
 */
const buildAlignmentFromMatches = (
  sourceLength: number,
  targetLength: number,
  matches: Array<{ sourceIndex: number; targetIndex: number }>
): TAlignmentEntry[] => {
  const alignment: TAlignmentEntry[] = []
  let sourcePointer = 0
  let targetPointer = 0

  /**
   * Helper function to create alignment entries for unmatched segments.
   * Pairs source and target elements greedily by position, marking extras as inserted/deleted.
   */
  const pushSegment = (
    sourceStart: number,
    sourceEnd: number,
    targetStart: number,
    targetEnd: number
  ) => {
    const sourceSize = sourceEnd - sourceStart
    const targetSize = targetEnd - targetStart
    const segmentSize = Math.max(sourceSize, targetSize)

    // Create alignment entries for each position in the segment
    for (let index = 0; index < segmentSize; index += 1) {
      const hasSource = sourceStart + index < sourceEnd
      const hasTarget = targetStart + index < targetEnd

      if (hasSource && hasTarget) {
        // Both source and target exist at this position - they are paired and changed
        alignment.push({
          sourceIndex: sourceStart + index,
          targetIndex: targetStart + index,
          status: EPDFPageStatus.CHANGED,
        })
        continue
      }

      if (hasSource) {
        // Only source exists - element was deleted
        alignment.push({
          sourceIndex: sourceStart + index,
          targetIndex: null,
          status: EPDFPageStatus.DELETED,
        })
        continue
      }

      // Only target exists - element was inserted
      alignment.push({
        sourceIndex: null,
        targetIndex: targetStart + index,
        status: EPDFPageStatus.INSERTED,
      })
    }
  }

  // Process all matches and fill gaps between them
  matches.forEach((match) => {
    // If there's a gap before this match, fill it with changed/deleted/inserted entries
    if (
      sourcePointer < match.sourceIndex ||
      targetPointer < match.targetIndex
    ) {
      pushSegment(
        sourcePointer,
        match.sourceIndex,
        targetPointer,
        match.targetIndex
      )
    }

    // Add the matched elements as equal
    alignment.push({
      sourceIndex: match.sourceIndex,
      targetIndex: match.targetIndex,
      status: EPDFPageStatus.EQUAL,
    })
    sourcePointer = match.sourceIndex + 1
    targetPointer = match.targetIndex + 1
  })

  // Handle any remaining unmatched elements after the last match
  if (sourcePointer < sourceLength || targetPointer < targetLength) {
    pushSegment(sourcePointer, sourceLength, targetPointer, targetLength)
  }

  return alignment
}

/**
 * High-level function that aligns two sequences using the LCS algorithm.
 * Combines LCS table building, match collection, and alignment generation.
 *
 * @param source - Source sequence to align
 * @param target - Target sequence to align
 * @param isEqual - Custom equality comparison function
 * @returns Complete alignment with status for each element pair
 */
const alignSequence = <T>(
  source: T[],
  target: T[],
  isEqual: (a: T, b: T) => boolean
) => {
  const dp = buildLcsTable(source, target, isEqual)
  const matches = collectLcsMatches(source, target, isEqual, dp)
  return buildAlignmentFromMatches(source.length, target.length, matches)
}

/**
 * Builds word-level operations (insert, delete, replace) between two word sequences.
 * Uses LCS to identify which words are identical, then groups unmatched words into operations.
 *
 * Operation Types:
 * - REPLACE: Different words at same position (both old and new present)
 * - DELETE: Words only in source
 * - INSERT: Words only in target
 *
 * Algorithm:
 * 1. Uses LCS to find matching words between sequences
 * 2. For each gap between matches, creates appropriate operation
 * 3. Preserves word boundaries for accurate diff representation
 *
 * @param sourceWords - Words from source text
 * @param targetWords - Words from target text
 * @returns Array of word-level operations describing the transformation
 */
const buildWordOps = (sourceWords: string[], targetWords: string[]) => {
  const isWordEqual = (a: string, b: string) => a === b
  const dp = buildLcsTable(sourceWords, targetWords, isWordEqual)
  const matches = collectLcsMatches(sourceWords, targetWords, isWordEqual, dp)
  const ops: IPDFWordOp[] = []

  /**
   * Creates operation(s) for a pair of word segments.
   * Determines operation type based on what content exists in old vs new.
   */
  const pushOps = (
    oldStart: number,
    oldEnd: number,
    newStart: number,
    newEnd: number
  ) => {
    const oldWords = sourceWords.slice(oldStart, oldEnd)
    const newWords = targetWords.slice(newStart, newEnd)
    const oldText = oldWords.join(' ').trim()
    const newText = newWords.join(' ').trim()

    if (oldText && newText) {
      // Both old and new text exist - this is a replacement
      ops.push({
        type: EDocumentComparisonChangeType.REPLACE,
        oldText,
        newText,
        oldWords,
        newWords,
        oldRange: [oldStart, oldEnd],
        newRange: [newStart, newEnd],
      })
      return
    }

    if (oldText) {
      // Only old text exists - this is a deletion
      ops.push({
        type: EDocumentComparisonChangeType.DELETE,
        oldText,
        oldWords,
        oldRange: [oldStart, oldEnd],
      })
      return
    }

    if (newText) {
      // Only new text exists - this is an insertion
      ops.push({
        type: EDocumentComparisonChangeType.INSERT,
        newText,
        newWords,
        newRange: [newStart, newEnd],
      })
    }
  }

  let sourcePointer = 0
  let targetPointer = 0

  // Process all matches and generate operations for gaps
  matches.forEach((match) => {
    if (
      sourcePointer < match.sourceIndex ||
      targetPointer < match.targetIndex
    ) {
      pushOps(
        sourcePointer,
        match.sourceIndex,
        targetPointer,
        match.targetIndex
      )
    }
    sourcePointer = match.sourceIndex + 1
    targetPointer = match.targetIndex + 1
  })

  // Handle remaining words after the last match
  if (
    sourcePointer < sourceWords.length ||
    targetPointer < targetWords.length
  ) {
    pushOps(
      sourcePointer,
      sourceWords.length,
      targetPointer,
      targetWords.length
    )
  }

  return ops
}

/**
 * Maps word operation types to change item types for the final comparison output.
 * INSERT operations are mapped to REPLACE for consistency in the change item format.
 *
 * @param wordOp - Word operation to classify
 * @returns Change type for use in comparison items
 */
const mapWordOpToChangeType = (wordOp: IPDFWordOp) => {
  switch (wordOp.type) {
    case EDocumentComparisonChangeType.REPLACE:
    case EDocumentComparisonChangeType.DELETE:
      return wordOp.type

    case EDocumentComparisonChangeType.INSERT:
    default:
      return EDocumentComparisonChangeType.REPLACE
  }
}

const getRangeBbox = (wordBboxes: TBbox[], range?: [number, number]) => {
  if (!range) return undefined
  const [start, end] = range
  const slice = wordBboxes.slice(start, end)
  if (slice.length === 0) return undefined
  const x0 = Math.min(...slice.map((bbox) => bbox[0]))
  const y0 = Math.min(...slice.map((bbox) => bbox[1]))
  const x1 = Math.max(...slice.map((bbox) => bbox[2]))
  const y1 = Math.max(...slice.map((bbox) => bbox[3]))
  return [x0, y0, x1, y1] as TBbox
}

/**
 * Creates a comparison change item from the provided parameters.
 * Validates that meaningful content exists (either old or new value) before creation.
 * Generates a unique identifier for each change item for tracking purposes.
 *
 * @param params - Configuration object containing change details, positions, and bounding boxes
 * @returns Change item ready for reporting, or null if no meaningful content exists
 */
const buildChangeItem = (params: {
  type: EDocumentComparisonChangeType
  oldValue?: string
  newValue?: string
  pageIndex?: number
  sourceLineIndex?: number
  targetLineIndex?: number
  sourceBbox?: [number, number, number, number]
  targetBbox?: [number, number, number, number]
}): IPDFCompareChangeItem | null => {
  const { oldValue, newValue } = params
  const hasOld = typeof oldValue === 'string' && oldValue.length > 0
  const hasNew = typeof newValue === 'string' && newValue.length > 0
  let type = params.type
  if (!hasOld && type === EDocumentComparisonChangeType.REPLACE) {
    type = EDocumentComparisonChangeType.INSERT
  }

  if (!hasNew && type === EDocumentComparisonChangeType.REPLACE) {
    type = EDocumentComparisonChangeType.DELETE
  }

  // Skip if neither old nor new value exists (empty change)
  if (!hasOld && !hasNew) return null

  return {
    type,
    uuid: crypto.randomUUID(),
    oldValue: hasOld ? oldValue : undefined,
    newValue: hasNew ? newValue : undefined,
    pageIndex: (params.pageIndex || 0) + 1, // Convert to 1-based indexing
    sourceLineIndex: hasOld ? params.sourceLineIndex : undefined,
    targetLineIndex: hasNew ? params.targetLineIndex : undefined,
    sourceBbox: hasOld ? params.sourceBbox : undefined,
    targetBbox: hasNew ? params.targetBbox : undefined,
  }
}

/**
 * Collects all lines from an inserted page as individual change items.
 * Used when an entire page is newly added in the target document.
 *
 * @param targetPage - Page info for the inserted page
 * @param targetIndex - Index of the inserted page
 * @returns Array of change items, one for each line in the inserted page
 */
const collectInsertedPageChanges = (
  targetPage: TPageInfo,
  targetIndex: number
) => {
  return targetPage.lines
    .map((line) =>
      buildChangeItem({
        type: EDocumentComparisonChangeType.INSERT,
        newValue: line.text,
        pageIndex: targetIndex,
        targetLineIndex: line.lineIndex,
        targetBbox: line.bbox,
      })
    )
    .filter((change): change is IPDFCompareChangeItem => change !== null)
}

/**
 * Collects all lines from a deleted page as individual change items.
 * Used when an entire page exists in the source but not in the target document.
 *
 * @param sourcePage - Page info for the deleted page
 * @param sourceIndex - Index of the deleted page
 * @returns Array of change items, one for each line in the deleted page
 */
const collectDeletedPageChanges = (
  sourcePage: TPageInfo,
  sourceIndex: number
) => {
  return sourcePage.lines
    .map((line) =>
      buildChangeItem({
        type: EDocumentComparisonChangeType.DELETE,
        oldValue: line.text,
        pageIndex: sourceIndex,
        sourceLineIndex: line.lineIndex,
        sourceBbox: line.bbox,
      })
    )
    .filter((change): change is IPDFCompareChangeItem => change !== null)
}

/**
 * Safely retrieves a line by its index with null safety.
 * Returns null if index is null or if the index is out of bounds.
 *
 * @param lines - Array of line info objects
 * @param index - Index to retrieve (can be null)
 * @returns Line at the index, or null if not found
 */
const getLineByIndex = (lines: TLineInfo[], index: number | null) => {
  if (index === null) return null
  return lines[index] ?? null
}

/**
 * Handles a line that exists in source but not in target (deleted line).
 * Creates a DELETE change item for the line with all its metadata.
 *
 * @param sourceLine - Line from the source page (or null if not found)
 * @param sourceIndex - Index of the source page containing the line
 * @returns Change item for the deleted line, or null if source line is null
 */
const handleDeletedLine = (
  sourceLine: TLineInfo | null,
  sourceIndex: number
) => {
  if (!sourceLine) return null
  return buildChangeItem({
    type: EDocumentComparisonChangeType.DELETE,
    oldValue: sourceLine.text,
    pageIndex: sourceIndex,
    sourceLineIndex: sourceLine.lineIndex,
    sourceBbox: sourceLine.bbox,
  })
}

/**
 * Handles a line that exists in target but not in source (inserted line).
 * Creates an INSERT change item for the line with all its metadata.
 *
 * @param targetLine - Line from the target page (or null if not found)
 * @param targetIndex - Index of the target page containing the line
 * @returns Change item for the inserted line, or null if target line is null
 */
const handleInsertedLine = (
  targetLine: TLineInfo | null,
  targetIndex: number
) => {
  if (!targetLine) return null
  return buildChangeItem({
    type: EDocumentComparisonChangeType.INSERT,
    newValue: targetLine.text,
    pageIndex: targetIndex,
    targetLineIndex: targetLine.lineIndex,
    targetBbox: targetLine.bbox,
  })
}

/**
 * Handles a line that exists in both source and target but with differences (changed line).
 * First checks if lines are identical when normalized; if so, returns empty array (no change).
 * Otherwise performs word-level diff:
 * - If word-level diff produces operations, creates change items for each operation
 * - If no word-level operations (shouldn't normally happen), creates single REPLACE change item for entire line
 *
 * @param sourceLine - Line from the source page (or null)
 * @param targetLine - Line from the target page (or null)
 * @param targetIndex - Index of the target page
 * @returns Array of change items describing the changes at word level, or empty if lines are identical
 */
const handleChangedLine = (
  sourceLine: TLineInfo | null,
  targetLine: TLineInfo | null,
  targetIndex: number
) => {
  if (!sourceLine || !targetLine) return []

  // If normalized text is identical, no changes to report
  if (sourceLine.normalizedText === targetLine.normalizedText) return []

  // Perform word-level diff to identify specific changes
  const wordOps = buildWordOps(sourceLine.words, targetLine.words)

  if (!wordOps.length) {
    // No word operations found, but lines are different - report entire line change
    const change = buildChangeItem({
      type: EDocumentComparisonChangeType.REPLACE,
      oldValue: sourceLine.text,
      newValue: targetLine.text,
      pageIndex: targetIndex,
      sourceLineIndex: sourceLine.lineIndex,
      targetLineIndex: targetLine.lineIndex,
      sourceBbox: sourceLine.bbox,
      targetBbox: targetLine.bbox,
    })

    return change ? [change] : []
  }

  // Convert word operations to change items
  return wordOps
    .map((wordOp) =>
      buildChangeItem({
        type: mapWordOpToChangeType(wordOp),
        oldValue: wordOp.oldText,
        newValue: wordOp.newText,
        pageIndex: targetIndex,
        sourceLineIndex: sourceLine.lineIndex,
        targetLineIndex: targetLine.lineIndex,
        sourceBbox:
          getRangeBbox(sourceLine.wordBboxes, wordOp.oldRange) ??
          sourceLine.bbox,
        targetBbox:
          getRangeBbox(targetLine.wordBboxes, wordOp.newRange) ??
          targetLine.bbox,
      })
    )
    .filter((change): change is IPDFCompareChangeItem => change !== null)
}

/**
 * Collects all changes between two aligned pages through line-level and word-level analysis.
 *
 * Process:
 * 1. Uses LCS algorithm to align lines within the two pages
 * 2. For each alignment entry, determines the status:
 *    - EQUAL: Lines are identical, skip
 *    - DELETED: Line only in source
 *    - INSERTED: Line only in target
 *    - CHANGED: Lines exist in both but differ
 * 3. For changed lines, performs word-level diff analysis
 * 4. Collects all change items and returns them in order
 *
 * @param sourcePage - Enriched page info from source document
 * @param targetPage - Enriched page info from target document
 * @param sourceIndex - Index of source page in document
 * @param targetIndex - Index of target page in document
 * @returns Array of all changes detected at line and word level
 */
const collectLineChangesForAlignedPages = (
  sourcePage: TPageInfo,
  targetPage: TPageInfo,
  sourceIndex: number,
  targetIndex: number
) => {
  // Align lines within the pages using normalized text comparison
  const lineAlignment = alignSequence(
    sourcePage.lines,
    targetPage.lines,
    (a, b) => a.normalizedText === b.normalizedText
  )

  const changes: IPDFCompareChangeItem[] = []

  // Process each alignment entry
  lineAlignment.forEach((lineEntry) => {
    // Skip lines that are identical
    if (lineEntry.status === EPDFPageStatus.EQUAL) return

    const sourceLine = getLineByIndex(sourcePage.lines, lineEntry.sourceIndex)
    const targetLine = getLineByIndex(targetPage.lines, lineEntry.targetIndex)

    if (lineEntry.status === EPDFPageStatus.DELETED) {
      // Line only exists in source
      const change = handleDeletedLine(sourceLine, sourceIndex)
      if (change) changes.push(change)
      return
    }

    if (lineEntry.status === EPDFPageStatus.INSERTED) {
      // Line only exists in target
      const change = handleInsertedLine(targetLine, targetIndex)
      if (change) changes.push(change)
      return
    }

    // Lines exist in both but differ - perform word-level diff
    const changeItems = handleChangedLine(sourceLine, targetLine, targetIndex)
    if (changeItems.length > 0) changes.push(...changeItems)
  })

  return changes
}

const mergeChangesByBbox = (changes: IPDFCompareChangeItem[]) => {
  const getBboxKey = (bbox?: TBbox) =>
    bbox ? bbox.map((value) => value.toFixed(3)).join(',') : ''

  const mergeText = (base?: unknown, next?: unknown) => {
    const baseText = typeof base === 'string' ? base : ''
    const nextText = typeof next === 'string' ? next : ''
    if (!nextText) return baseText || undefined
    if (!baseText) return nextText
    if (baseText.includes(nextText)) return baseText
    return `${baseText}\n${nextText}`
  }

  const map = new Map<string, IPDFCompareChangeItem>()
  changes.forEach((change) => {
    const key = [
      change.type,
      change.pageIndex ?? '',
      change.sourceLineIndex ?? '',
      change.targetLineIndex ?? '',
      getBboxKey(change.sourceBbox),
      getBboxKey(change.targetBbox),
    ].join('|')

    const existing = map.get(key)
    if (!existing) {
      map.set(key, { ...change })
      return
    }

    existing.oldValue = mergeText(existing.oldValue, change.oldValue)
    existing.newValue = mergeText(existing.newValue, change.newValue)
  })

  return Array.from(map.values())
}

/**
 * Main PDF Document Comparison Function
 *
 * Performs comprehensive three-level comparison between two PDF documents:
 * 1. PAGE LEVEL: Uses LCS to align pages by normalized text content
 * 2. LINE LEVEL: For aligned pages, uses LCS to align lines by normalized text
 * 3. WORD LEVEL: For changed lines, performs word-level diff with operation classification
 *
 * Process:
 * 1. Validates that both source and target documents exist
 * 2. Extracts and normalizes all pages and lines from both documents
 * 3. Aligns pages using LCS algorithm
 * 4. For each page alignment:
 *    - If page only in target: Record all lines as insertions
 *    - If page only in source: Record all lines as deletions
 *    - If pages aligned: Perform line-level and word-level comparison
 * 5. Returns comprehensive list of all changes with full metadata
 *
 * @param source - Source PDF parser result (or null/undefined)
 * @param target - Target PDF parser result (or null/undefined)
 * @returns Array of change items documenting all differences at word level,
 *          including type (insert/delete/replace), old/new values, page/line indices,
 *          and bounding box information for document positioning
 *
 * @example
 * const changes = diffPDFDocuments(sourcePdf, targetPdf);
 * changes.forEach(change => {
 *   console.log(`${change.type}: "${change.oldValue}" -> "${change.newValue}"`);
 *   console.log(`  Location: Page ${change.pageIndex}, Line ${change.targetLineIndex}`);
 * });
 */
export const diffPDFDocuments = (
  source: IPDFParserResult | null | undefined,
  target: IPDFParserResult | null | undefined
): IPDFCompareChangeItem[] => {
  // Handle null/undefined inputs
  if (!source || !target) return []

  // Extract and build enriched page information from both documents
  const sourcePages = source.pages.map((page) => buildPageInfo(page))
  const targetPages = target.pages.map((page) => buildPageInfo(page))

  // Align pages using normalized text content for page matching
  const pageAlignment = alignSequence(
    sourcePages,
    targetPages,
    (a, b) => a.normalizedText === b.normalizedText
  )

  const changesList: IPDFCompareChangeItem[] = []

  // Process each page alignment entry
  pageAlignment.forEach((pageEntry) => {
    const sourceIndex = pageEntry.sourceIndex
    const targetIndex = pageEntry.targetIndex

    if (sourceIndex === null && targetIndex !== null) {
      // Page only exists in target - entire page is new
      const targetPage = targetPages[targetIndex]
      changesList.push(...collectInsertedPageChanges(targetPage, targetIndex))
      return
    }

    if (targetIndex === null && sourceIndex !== null) {
      // Page only exists in source - entire page was removed
      const sourcePage = sourcePages[sourceIndex]
      changesList.push(...collectDeletedPageChanges(sourcePage, sourceIndex))
      return
    }

    // Both pages exist - perform detailed line and word comparison
    if (sourceIndex === null || targetIndex === null) return

    const sourcePage = sourcePages[sourceIndex]
    const targetPage = targetPages[targetIndex]
    const changes = collectLineChangesForAlignedPages(
      sourcePage,
      targetPage,
      sourceIndex,
      targetIndex
    )
    changesList.push(...changes)
  })

  return mergeChangesByBbox(changesList)
}
